import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import { Box, Card, CardContent, Divider, Link, Stack, TableContainer, Typography } from '@mui/material'

import { DiscontinuedSkullIcon } from '../../../../components/DiscontinuedSkullIcon/DiscontinuedSkullIcon'
import { GRTable, GRTableColumn, SortOrder, TableSortValues } from '../../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { DescriptionHoverCard } from '../DescriptionHoverCard'
import { formatAdoptionPct } from '../helpers/catalogueUtils'
import { DEFAULT_TAG_GROUP_COLOR } from '../helpers/tagGroups'

export interface EventTypeBlueprintRow {
  id: string
  name: string
  // Detail-page target, built by the page so the segment filters ride along.
  to: string
  state?: unknown
  discontinued: boolean
  // Segment-scoped adoption share (0–1), or null when the segment size is unknown.
  adoptionPct: number | null
  // Windowed adopting games; null when the backend omits the field.
  adoptingGames: number | null
  // Windowed first-time adopters; null when the backend omits the field.
  newAdopters: number | null
  // Localized glossary copy (HTML). The name's hover card omits the description section when absent.
  description?: string
}

interface EventTypeBlueprintsCardProps {
  rows: EventTypeBlueprintRow[]
  isLoading?: boolean
  // Hover-card breadcrumb. The same for every row, since they all share the page's event type.
  categoryName?: string
  eventTypeName?: string
  categoryColorHex?: string
}

// Center align doesn't trigger MUI's row-reverse flex quirk, so the sort arrow
// follows the label naturally. Used for the short numeric columns (adoption %,
// counts) — centering reads better for small figures than right-aligning them
// against a wide column.
const CENTER_ALIGNED_PROPS = { align: 'center' as const }

// Passed as GRTable custom props rather than captured by the column definitions: the
// columns live in state (for sorting), so captured values would go stale when in-app
// navigation reuses the card for another event type.
interface EventTypeBlueprintsTableProps {
  hoverCardTrail: string[]
  categoryColorHex: string
}

/**
 * The blueprints belonging to one event type, as the drill-down step from the
 * event-type page to a blueprint page. Card header copy + edge-to-edge `GRTable`
 * (the GR "header + table" card pattern).
 */
export const EventTypeBlueprintsCard: React.FC<EventTypeBlueprintsCardProps> = React.memo(
  ({ rows, isLoading, categoryName, eventTypeName, categoryColorHex = DEFAULT_TAG_GROUP_COLOR }) => {
    const { t } = useTranslation()
    const containerRef = useRef<HTMLDivElement>(null)

    const customTableProps: EventTypeBlueprintsTableProps = useMemo(
      () => ({
        hoverCardTrail: [categoryName, eventTypeName].filter((name): name is string => Boolean(name)),
        categoryColorHex,
      }),
      [categoryName, eventTypeName, categoryColorHex]
    )

    const tableColumns: GRTableColumn<EventTypeBlueprintRow, EventTypeBlueprintsTableProps>[] = useMemo(
      () => [
        {
          labelAccessor: t('market-analytics:granularity_blueprint'),
          accessor: ({ row, customTableProps }) => (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              {/* No stat tiles: the row's own columns already show the figures. The
                  card sets its own width, so the tooltip's max-width is disabled. */}
              <GRTooltip
                content={
                  <DescriptionHoverCard
                    name={row.name}
                    trail={customTableProps?.hoverCardTrail ?? []}
                    colorHex={customTableProps?.categoryColorHex ?? DEFAULT_TAG_GROUP_COLOR}
                    discontinued={row.discontinued}
                    description={row.description}
                    descriptionLabel={t('market-analytics:detail_panel_what_this_blueprint_is')}
                    isRichTextDescription
                  />
                }
                disableMaxWidth
                styles={{ minWidth: 0 }}
              >
                <Link component={RouterLink} to={row.to} state={row.state} underline="hover" color="primary" variant="body2" sx={{ fontWeight: 'bold' }}>
                  {row.name}
                </Link>
              </GRTooltip>
              {row.discontinued ? (
                <GRTooltip content={t('market-analytics:discontinued_blueprint_tooltip')} styles={{ display: 'inline-flex', cursor: 'help', flexShrink: 0 }}>
                  <DiscontinuedSkullIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </GRTooltip>
              ) : null}
            </Stack>
          ),
          sortable: true,
          sortAccessor: ({ row }) => row.name,
          cellProps: { align: 'left' },
          headerCellProps: { align: 'left', sx: { minWidth: 260 } },
          defaultSortOrder: SortOrder.ASC,
        },
        {
          // Per blueprint family, not per event type: the tile above counts any event of
          // the type, while each row here counts only the games running that one family.
          labelAccessor: () => (
            <GRTooltip content={t('market-analytics:blueprint_family_row_adoption_tooltip')}>{t('market-analytics:detail_stat_adoption_label')}</GRTooltip>
          ),
          accessor: ({ row }) => formatAdoptionPct(row.adoptionPct),
          sortable: true,
          sortAccessor: ({ row }) => (row.adoptionPct === null ? TableSortValues.AlwaysLast : row.adoptionPct),
          cellProps: CENTER_ALIGNED_PROPS,
          headerCellProps: CENTER_ALIGNED_PROPS,
          sortOrder: SortOrder.DESC,
          defaultSortOrder: SortOrder.DESC,
        },
        {
          labelAccessor: () => (
            <GRTooltip content={t('market-analytics:adopting_games_in_segment_tooltip')}>{t('market-analytics:adopting_games_in_segment')}</GRTooltip>
          ),
          accessor: ({ row }) => (row.adoptingGames === null ? '—' : row.adoptingGames),
          sortable: true,
          sortAccessor: ({ row }) => (row.adoptingGames === null ? TableSortValues.AlwaysLast : row.adoptingGames),
          cellProps: CENTER_ALIGNED_PROPS,
          headerCellProps: CENTER_ALIGNED_PROPS,
          defaultSortOrder: SortOrder.DESC,
        },
        {
          labelAccessor: () => <GRTooltip content={t('market-analytics:new_adopters_tooltip')}>{t('market-analytics:new_adopters')}</GRTooltip>,
          accessor: ({ row }) => (row.newAdopters === null ? '—' : row.newAdopters),
          sortable: true,
          sortAccessor: ({ row }) => (row.newAdopters === null ? TableSortValues.AlwaysLast : row.newAdopters),
          cellProps: CENTER_ALIGNED_PROPS,
          headerCellProps: CENTER_ALIGNED_PROPS,
          defaultSortOrder: SortOrder.DESC,
        },
      ],
      [t]
    )

    const [columns, setColumns] = useState<GRTableColumn<EventTypeBlueprintRow, EventTypeBlueprintsTableProps>[]>(tableColumns)

    const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<EventTypeBlueprintRow, EventTypeBlueprintsTableProps>[]) => {
      setColumns(updatedColumns)
    }, [])

    return (
      <Card>
        <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h3" gutterBottom>
            {t('market-analytics:event_type_blueprints_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('market-analytics:event_type_blueprints_description')}
          </Typography>
        </CardContent>
        <Divider />
        <TableContainer component={Box} ref={containerRef}>
          <GRTable
            rows={rows}
            columns={columns}
            customProps={customTableProps}
            onColumnsUpdated={handleColumnsUpdate}
            rowIdKey="id"
            scroller={containerRef}
            isLoading={isLoading}
            noRowsLabel={t('market-analytics:no_data_available')}
            hoverable
            striped
          />
        </TableContainer>
      </Card>
    )
  }
)
